import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { toast } from 'sonner';

export interface ClubInvitation {
  id: string;
  club_id: string;
  inviter_id: string;
  invitee_id: string;
  message: string | null;
  status: string;
  reviewed_at: string | null;
  created_at: string;
  updated_at: string;
  club?: {
    id: string;
    name: string;
    avatar: string | null;
    sport: string;
  } | null;
  inviter?: {
    id: string;
    name: string;
    avatar: string | null;
  } | null;
  invitee?: {
    id: string;
    name: string;
    avatar: string | null;
  } | null;
}

// Hook to fetch invitations sent by the current user for a specific club
export function useSentClubInvitations(clubId: string) {
  const { profile } = useAuth();

  return useQuery({
    queryKey: ['club-invitations-sent', clubId, profile?.id],
    queryFn: async () => {
      if (!profile?.id || !clubId) return [];

      const { data, error } = await supabase
        .from('club_invitations')
        .select(`
          *,
          invitee:profiles!club_invitations_invitee_id_fkey(id, name, avatar)
        `)
        .eq('club_id', clubId)
        .eq('inviter_id', profile.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as ClubInvitation[];
    },
    enabled: !!clubId && !!profile?.id,
  });
}

// Hook to fetch invitations received by the current user
export function useMyClubInvitations(status?: string) {
  const { profile } = useAuth();

  return useQuery({
    queryKey: ['club-invitations-received', profile?.id, status],
    queryFn: async () => {
      if (!profile?.id) return [];

      let query = supabase
        .from('club_invitations')
        .select(`
          *,
          club:clubs(id, name, avatar, sport),
          inviter:profiles!club_invitations_inviter_id_fkey(id, name, avatar)
        `)
        .eq('invitee_id', profile.id)
        .order('created_at', { ascending: false });

      if (status) {
        query = query.eq('status', status);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as ClubInvitation[];
    },
    enabled: !!profile?.id,
  });
}

// Hook to get pending invitations count
export function usePendingClubInvitationsCount() {
  const { profile } = useAuth();

  return useQuery({
    queryKey: ['club-invitations-count', profile?.id],
    queryFn: async () => {
      if (!profile?.id) return 0;

      const { count, error } = await supabase
        .from('club_invitations')
        .select('*', { count: 'exact', head: true })
        .eq('invitee_id', profile.id)
        .eq('status', 'pending');

      if (error) throw error;
      return count || 0;
    },
    enabled: !!profile?.id,
  });
}

/**
 * Returns both the pending count AND the latest pending created_at,
 * so the Header badge can decide whether the user has already
 * "seen" everything currently pending.
 */
export function usePendingClubInvitationsSummary() {
  const { profile } = useAuth();

  return useQuery({
    queryKey: ['club-invitations-summary', profile?.id],
    queryFn: async () => {
      if (!profile?.id) return { count: 0, latestCreatedAt: null as string | null };

      const { data, error } = await supabase
        .from('club_invitations')
        .select('created_at')
        .eq('invitee_id', profile.id)
        .eq('status', 'pending')
        .order('created_at', { ascending: false });

      if (error) throw error;
      const rows = data || [];
      return {
        count: rows.length,
        latestCreatedAt: rows[0]?.created_at ?? null,
      };
    },
    enabled: !!profile?.id,
  });
}

// Hook to send a club invitation
export function useSendClubInvitation() {
  const queryClient = useQueryClient();
  const { profile } = useAuth();

  return useMutation({
    mutationFn: async ({
      clubId,
      inviteeId,
      message,
    }: {
      clubId: string;
      inviteeId: string;
      message?: string;
    }) => {
      if (!profile?.id) throw new Error('Chưa đăng nhập');

      const { data, error } = await supabase
        .from('club_invitations')
        .insert({
          club_id: clubId,
          inviter_id: profile.id,
          invitee_id: inviteeId,
          message: message || null,
        })
        .select()
        .single();

      if (error) {
        if (error.code === '23505') {
          throw new Error('Người chơi này đã được mời vào CLB');
        }
        throw error;
      }
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['club-invitations-sent', variables.clubId] });
      toast.success('Đã gửi lời mời');
    },
    onError: (error: Error) => {
      toast.error('Lỗi gửi lời mời', { description: error.message });
    },
  });
}

// Hook to respond to a club invitation
export function useRespondToClubInvitation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      invitationId,
      clubId,
      accepted,
    }: {
      invitationId: string;
      clubId: string;
      accepted: boolean;
    }) => {
      // Update invitation status
      const { error: updateError } = await supabase
        .from('club_invitations')
        .update({
          status: accepted ? 'accepted' : 'rejected',
          reviewed_at: new Date().toISOString(),
        })
        .eq('id', invitationId);

      if (updateError) throw updateError;

      // If accepted, add as member
      if (accepted) {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('Chưa đăng nhập');

        // CRITICAL: Get profile.id from profiles table, NOT auth.users.id
        // club_members.profile_id is FK to profiles.id, not auth.users.id
        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('id')
          .eq('user_id', user.id)
          .single();

        if (profileError || !profile) {
          throw new Error('Không tìm thấy hồ sơ người dùng');
        }

        const { error: memberError } = await supabase
          .from('club_members')
          .insert({
            club_id: clubId,
            profile_id: profile.id, // ✅ Correct: use profile.id, not user.id
            role: 'member',
          });

        if (memberError && memberError.code !== '23505') {
          throw memberError;
        }
      }

      return { accepted };
    },
    onSuccess: (result, variables) => {
      queryClient.invalidateQueries({ queryKey: ['club-invitations-received'] });
      queryClient.invalidateQueries({ queryKey: ['club-invitations-count'] });
      queryClient.invalidateQueries({ queryKey: ['club-members', variables.clubId] });
      queryClient.invalidateQueries({ queryKey: ['my-club-membership', variables.clubId] });
      queryClient.invalidateQueries({ queryKey: ['my-clubs'] });
      
      toast.success(result.accepted ? 'Đã tham gia CLB' : 'Đã từ chối lời mời');
    },
    onError: (error: Error) => {
      toast.error('Lỗi xử lý lời mời', { description: error.message });
    },
  });
}

// Hook to cancel a club invitation
export function useCancelClubInvitation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ invitationId, clubId }: { invitationId: string; clubId: string }) => {
      const { error } = await supabase
        .from('club_invitations')
        .update({ status: 'cancelled' })
        .eq('id', invitationId);

      if (error) throw error;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['club-invitations-sent', variables.clubId] });
      toast.success('Đã hủy lời mời');
    },
    onError: (error: Error) => {
      toast.error('Lỗi hủy lời mời', { description: error.message });
    },
  });
}
